/**
 * Version Check Hook
 * Detects when a new version of the app is deployed
 */

import { useEffect, useState } from "react";

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const LOCAL_STORAGE_KEY = "app-version";

export const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkVersion = async () => {
    try {
      // Fetch version.json with cache-busting timestamp
      const response = await fetch(`/version.json?t=${Date.now()}`);
      const data = await response.json();

      const currentVersion = localStorage.getItem(LOCAL_STORAGE_KEY);
      const newVersion = `${data.version}-${data.buildTime}`;

      if (!currentVersion) {
        // First load, store version
        localStorage.setItem(LOCAL_STORAGE_KEY, newVersion);
      } else if (currentVersion !== newVersion) {
        // New version detected
        console.log("New version detected:", {
          current: currentVersion,
          new: newVersion,
        });
        setUpdateAvailable(true);
      }
    } catch (error) {
      console.error("Error checking version:", error);
    }
  };

  const reloadApp = async () => {
    try {
      console.log(
        "[VersionCheck] New version available - reloading to pick up new assets",
      );

      // Update stored version to prevent reload loop
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`);
        const data = await response.json();
        const newVersion = `${data.version}-${data.buildTime}`;
        localStorage.setItem(LOCAL_STORAGE_KEY, newVersion);
      } catch (error) {
        console.error("[VersionCheck] Error updating version:", error);
      }

      // Simple reload to pick up new JS/CSS assets.
      // No sign-out or storage clearing — the session is still valid,
      // the user just needs fresh frontend code.
      window.location.reload();
    } catch (error) {
      // If anything fails, force a hard reload as fallback
      console.error(
        "[VersionCheck] Error during reload, forcing refresh:",
        error,
      );
      window.location.reload();
    }
  };

  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Check periodically
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return { updateAvailable, reloadApp };
};
